import { getColorScheme, getSparkChartOptions } from '../utils';
import { useMemo } from 'react';
import { useAppSelector } from '@customer/store/hooks';
import { useTranslation } from 'react-i18next';
import {
  HealthSelfTestSparkLineQueryParams,
  ScenarioTimeFrame,
  StatsScenario,
  useGetScenarioTimeSeriesQuery,
} from '@customer/store/apis/scenariosApi';
import { Line } from 'react-chartjs-2';
import { FaultLevel } from '@customer/store/slices/globalSlice';
import { ChartColorScheme } from '../config';
import { ChartOptions, ChartData, ChartDataset } from 'chart.js';
import { FilterGroup } from '@customer/filters/types';
import useFilters from '@customer/filters/useFilters';
import { commonLineChartDatasetOptions } from '../line-chart-scenario/LineChartScenario';
import ChartManager from '../chart-manager/ChartManager';

interface SparkLineProps {
  faultLevel: FaultLevel;
  colorScheme: ChartColorScheme;
  getCategoryLabel: (id: string) => string;
  getDataSetLabel?: (id: string) => string;
}

export const SparkLine = ({
  faultLevel,
  colorScheme,
  getCategoryLabel,
  getDataSetLabel,
}: SparkLineProps) => {
  const { t } = useTranslation();

  const { siteId, dateTimeFormat, siteTimezone } = useAppSelector(state => state.auth);
  const { darkTheme } = useAppSelector(state => state.prefs);
  const { timeRange } = useAppSelector(state => state.global);
  const filters = useFilters<HealthSelfTestSparkLineQueryParams>(FilterGroup.HealthSelfTest);

  const { data: chartData, isFetching } = useGetScenarioTimeSeriesQuery(
    {
      scenario: StatsScenario.AssetsWithFaults,
      query: {
        ...filters,
        faultLevels: [faultLevel],
        timeRange,
      },
    },
    { refetchOnMountOrArgChange: 60, skip: typeof siteId === 'undefined' },
  );

  const data = useMemo<ChartData<'line', ScenarioTimeFrame[]>>(() => {
    let displayLabels: string[] = [];
    const dataSets: ChartDataset<'line', ScenarioTimeFrame[]>[] = [];

    if (chartData) {
      displayLabels = chartData.timestamps.map((label: string) => getCategoryLabel(label));

      const stackedColors = getColorScheme(colorScheme);

      for (let i = 0; i < chartData.dataSets.length; i++) {
        const item = chartData.dataSets[i];
        const themeColors = stackedColors[i % stackedColors.length];
        const color = darkTheme ? themeColors[1] : themeColors[0];
        const label = getDataSetLabel?.(item.label) ?? item.label;

        dataSets.push({
          ...commonLineChartDatasetOptions,
          label,
          data: item.frames,
          backgroundColor: color,
          borderColor: color,
          stack: '',
        });
      }
    }

    return {
      labels: displayLabels,
      datasets: dataSets,
    };
  }, [darkTheme, colorScheme, chartData, getDataSetLabel, getCategoryLabel]);

  const chartOptions = useMemo<ChartOptions<'line'>>(() => {
    return getSparkChartOptions(darkTheme, siteTimezone, dateTimeFormat, t);
  }, [darkTheme, siteTimezone, dateTimeFormat, t]);

  const numLabels = data?.labels?.length ?? 0;

  return (
    <ChartManager
      isLoading={typeof chartData === 'undefined'}
      isFetching={isFetching}
      hasData={numLabels > 1}
      height={64} // This pixel value is specifically to mimic the height of the sister tab insights.
    >
      <Line options={chartOptions} data={data} />
    </ChartManager>
  );
};

export default SparkLine;
